import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetCategoryQuery, useGetCategoryPropertiesQuery, useUpdateCategoryPropertiesMutation, useDeletePropertyMutation } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import ImageUpload from '../../../components/ImageUpload';
import { showSuccess, showError } from '../../../utils/toast';
import { getImageUrl } from '../../../utils/imageHelpers';
import { useLanguage } from '../../../context/LanguageContext';

interface PropertyRow {
  id?: number;
  name: string;
  name_ar: string;
  main_title: string;
  main_title_ar: string;
  icon: File | null;
  existingIcon?: string;
  is_lock: boolean;
}

const AdminCategoryProperties: React.FC = () => {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const { id } = useParams<{ id: string }>();
  const categoryId = parseInt(id!);
  
  const { data: categoryData, isLoading: isLoadingCategory } = useGetCategoryQuery(categoryId);
  const { data: propertiesData, isLoading: isLoadingProperties, refetch } = useGetCategoryPropertiesQuery(categoryId);
  const [updateProperties, { isLoading: isUpdating }] = useUpdateCategoryPropertiesMutation();
  const [deleteProperty] = useDeletePropertyMutation();

  const [properties, setProperties] = useState<PropertyRow[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    if (propertiesData?.data) {
      const props = propertiesData.data.map((prop: any) => ({
        id: prop.id,
        name: prop.name || '',
        name_ar: prop.name_ar || '',
        main_title: prop.main_title || '',
        main_title_ar: prop.main_title_ar || '',
        icon: null,
        existingIcon: prop.icon ? (prop.icon.includes('/uploads/') ? prop.icon.split('/uploads/')[1] : prop.icon) : undefined,
        is_lock: prop.is_lock === 1 || prop.is_lock === true,
      }));
      if (props.length === 0) {
        props.push({
          name: '',
          name_ar: '',
          main_title: '',
          main_title_ar: '',
          icon: null,
          is_lock: false,
        });
      }
      setProperties(props);
    }
  }, [propertiesData]);

  const handleInputChange = (index: number, field: keyof PropertyRow, value: string | boolean) => {
    setProperties(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleIconChange = (index: number, file: File | null) => {
    setProperties(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], icon: file, existingIcon: file ? undefined : updated[index].existingIcon };
      return updated;
    });
  };

  const handleRemoveIcon = (index: number) => {
    setProperties(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], existingIcon: undefined, icon: null };
      return updated;
    });
  };

  const addRow = () => {
    setProperties(prev => [...prev, {
      name: '',
      name_ar: '',
      main_title: '',
      main_title_ar: '',
      icon: null,
      is_lock: false,
    }]);
  };

  const removeRow = async (index: number) => {
    const property = properties[index];
    if (property.id) {
      // Delete from backend
      const confirmed = window.confirm(t('Are you sure you want to delete? It may affect your products information'));
      if (!confirmed) return;

      try {
        await deleteProperty(property.id).unwrap();
        showSuccess(t('Property deleted successfully!'));
        refetch();
      } catch (err: any) {
        showError(err?.data?.message || t('Failed to delete property. Please try again.'));
      }
    } else {
      // Just remove from UI
      setProperties(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    setProperties(prev => prev.map(p => ({ ...p, is_lock: checked })));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const submitData = new FormData();
      
      properties.forEach((prop, index) => {
        if (prop.id) {
          submitData.append(`property_id[${index}]`, prop.id.toString());
        }
        submitData.append(`name[${index}]`, prop.name);
        submitData.append(`name_ar[${index}]`, prop.name_ar);
        submitData.append(`main_title[${index}]`, prop.main_title || '');
        submitData.append(`main_title_ar[${index}]`, prop.main_title_ar || '');
        if (prop.icon) {
          submitData.append(`icon[${index}]`, prop.icon);
        }
        submitData.append(`is_lock[${index}]`, prop.is_lock ? '1' : '0');
      });

      const result = await updateProperties({ id: categoryId, data: submitData }).unwrap();
      showSuccess(result.message || t('Properties updated successfully!'));
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || t('Failed to update properties. Please try again.'));
    }
  };

  if (isLoadingCategory || isLoadingProperties) {
    return <PageLoader />;
  }

  const category = categoryData?.data;

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {(lang === 'ar' ? (category?.title_ar || category?.name_ar) : (category?.name || category?.title)) || category?.name || category?.title} {t('Category - Properties')}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }} className="text-muted text-hover-primary">{t('Dashboard')}</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/categories'); }} className="text-muted text-hover-primary">{t('Categories')}</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-body p-5">
                <form onSubmit={handleSubmit} id="kt_modal_new_target_form" className="form fv-plugins-bootstrap5 fv-plugins-framework">
                  <button type="button" className="btn btn-base float-end btn-sm" onClick={addRow}>
                    + {t('Add New')}
                  </button>
                  <br /><br />

                  <div className="row my-3">
                    <div className="col-md-2"><h6>{t('Property Name')}</h6></div>
                    <div className="col-md-2"><h6>{t('Property Name Ar')}</h6></div>
                    <div className="col-md-2"><h6>{t('Parent Title')} <small>({t('optional')})</small></h6></div>
                    <div className="col-md-2"><h6>{t('Parent Title Ar')}<small>({t('optional')})</small></h6></div>
                    <div className="col-md-2"><h6>{t('Icon')} <small>({t('optional')})</small></h6></div>
                    <div className="col-md-2">
                      <h6>{t('Is Locked')}</h6>
                      <span>{t('All')}</span> <input
                        className="form-check-input border-base"
                        type="checkbox"
                        id="checkAll"
                        checked={selectAll}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    </div>
                  </div>

                  {properties.map((property, index) => (
                    <div key={index} className="row my-5">
                      <div className="col-md-2">
                        <input
                          type="text"
                          name={`name[${index}]`}
                          className="form-control"
                          placeholder={t('Enter Name')}
                          value={property.name}
                          onChange={(e) => handleInputChange(index, 'name', e.target.value)}
                          required
                        />
                      </div>
                      <div className="col-md-2">
                        <input
                          type="text"
                          name={`name_ar[${index}]`}
                          className="form-control"
                          placeholder={t('Enter Name')}
                          value={property.name_ar}
                          onChange={(e) => handleInputChange(index, 'name_ar', e.target.value)}
                          required
                        />
                      </div>
                      <div className="col-md-2">
                        <input
                          type="text"
                          name={`main_title[${index}]`}
                          className="form-control"
                          placeholder={t('Enter Main Title')}
                          value={property.main_title}
                          onChange={(e) => handleInputChange(index, 'main_title', e.target.value)}
                        />
                      </div>
                      <div className="col-md-2">
                        <input
                          type="text"
                          name={`main_title_ar[${index}]`}
                          className="form-control"
                          placeholder={t('Enter Main Title')}
                          value={property.main_title_ar}
                          onChange={(e) => handleInputChange(index, 'main_title_ar', e.target.value)}
                        />
                      </div>
                      <div className="col-md-2">
                        <ImageUpload
                          existingImage={property.existingIcon}
                          onImageChange={(file) => handleIconChange(index, file)}
                          onRemoveExisting={() => handleRemoveIcon(index)}
                          label=""
                          name={`icon[${index}]`}
                        />
                      </div>
                      <div className="col-md-1">
                        <input
                          className="form-check-input border-base mt-2 action_ids"
                          type="checkbox"
                          name={`is_lock[${index}]`}
                          checked={property.is_lock}
                          onChange={(e) => handleInputChange(index, 'is_lock', e.target.checked)}
                        />
                      </div>
                      <div className="col-md-1">
                        <button
                          type="button"
                          className="btn btn-base float-end btn-sm"
                          onClick={() => removeRow(index)}
                        >
                          - {t('Remove')}
                        </button>
                      </div>
                    </div>
                  ))}

                  <div className="text-center mt-5">
                    <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/categories'); }} className="btn btn-light me-3">
                      {t('Back')}
                    </a>
                    <button type="submit" className="btn btn-base" disabled={isUpdating}>
                      <span className="indicator-label">{t('Update')}</span>
                      {isUpdating && (
                        <span className="indicator-progress">
                          {t('Please wait...')}
                          <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCategoryProperties;

